/**
 * DebtEquityChart — Debt/Equity ratio per year with average reference line.
 *
 * Debt/Equity = Total Liabilities / Shareholders Funds
 */
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import ChartCard, { FocalBadge, focalTickFactory } from './ChartCard';
import ChartTooltip from './ChartTooltip';
import { isEstimatedValue, ESTIMATED_FILL_OPACITY } from './chartNotes';
import FinancialNoData from '../FinancialNoData';
import { formatFiscalYear } from '../utils/formatters';
import { PEER_COLORS, PEER_BAR_UNIFORM, BAR_MAX_SIZE, gridProps, xAxisProps, yAxisProps, zeroLineProps, refLineProps, CHART_INK, CHART_TYPE } from './chartTheme';
import type { UnifiedFinancialRecord, RegionConfig } from '../../../types/financial';

interface Props {
  data: UnifiedFinancialRecord[];
  region?: RegionConfig;
  companyName?: string;
  competitorData?: Record<string, UnifiedFinancialRecord[]>;
}

function latestDebtEquity(records: UnifiedFinancialRecord[]): number | null {
  const sorted = [...records].sort((a, b) => b.year - a.year);
  for (const r of sorted) {
    const equity = r.shareholders_funds;
    const debt =
      r.total_liabilities ??
      ((r.total_current_liabilities ?? 0) + (r.total_non_current_liabilities ?? 0));
    if (debt != null && equity != null && equity !== 0) {
      return debt / equity;
    }
  }
  return null;
}

export default function DebtEquityChart({ data, region, companyName, competitorData }: Props) {
  const isPeerMode =
    !!competitorData && Object.keys(competitorData).length > 0 && !!companyName;

  if (isPeerMode) {
    const focalDe = latestDebtEquity(data);
    // Insufficient peer data is a feature-not-available case, not a no-data one.
    if (focalDe == null) return null;

    const peerRows = [
      { company: companyName!, de: parseFloat(focalDe.toFixed(3)), isFocal: true },
      ...Object.entries(competitorData!)
        .map(([name, records]) => {
          const v = latestDebtEquity(records);
          return v != null
            ? { company: name, de: parseFloat(v.toFixed(3)), isFocal: false }
            : null;
        })
        .filter((r): r is { company: string; de: number; isFocal: boolean } => r != null),
    ].sort((a, b) => a.de - b.de);

    if (peerRows.length < 2) return null;

    const avg = peerRows.reduce((s, r) => s + r.de, 0) / peerRows.length;

    return (
      <ChartCard
        title="Debt/Equity — Peer Comparison"
        subtitle="Latest leverage ratio per company, sorted ascending (lower leverage first)."
        action={<FocalBadge name={companyName!} />}
      >
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={peerRows} margin={{ top: 24, right: 16, left: 8, bottom: 60 }}>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="company" interval={0} height={70} tick={focalTickFactory(companyName!)} />
            <YAxis
              {...yAxisProps}
              width={52}
              tickFormatter={(v) => v?.toFixed(2)}
              label={{ value: 'D/E ratio', angle: -90, position: 'insideLeft', style: { fontSize: CHART_TYPE.axisTick, fill: CHART_INK.muted } }}
            />
            <ReferenceLine y={0} {...zeroLineProps} />
            <ReferenceLine
              y={avg}
              {...refLineProps}
              label={{ value: `Avg ${avg.toFixed(2)}`, position: 'right', fill: CHART_INK.muted, fontSize: CHART_TYPE.axisTick }}
            />
            <Tooltip content={<ChartTooltip format={(v) => v.toFixed(3)} />} />
            <Bar dataKey="de" name="D/E" radius={[4, 4, 0, 0]} maxBarSize={BAR_MAX_SIZE}>
              <LabelList
                dataKey="de"
                position="top"
                formatter={(v: number) => v?.toFixed(2)}
                style={{ fontSize: CHART_TYPE.dataLabel, fill: CHART_INK.label, fontWeight: 600 }}
              />
              {/* One uniform tone for every company; focal marked by the ★ tick. */}
              {peerRows.map((row, i) => (
                <Cell
                  key={i}
                  fill={PEER_BAR_UNIFORM}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    );
  }

  const chartData = data
    .map((d) => {
      const equity = d.shareholders_funds;
      const debt = (d.total_liabilities ?? ((d.total_current_liabilities ?? 0) + (d.total_non_current_liabilities ?? 0))) || null;
      const ratio = debt != null && equity != null && equity !== 0 ? parseFloat((debt / equity).toFixed(3)) : null;
      return {
        year: formatFiscalYear(d.year, region),
        ratio,
        isNeg: ratio != null && ratio < 0,
        estimated: isEstimatedValue(d.data_notes, 'debt_equity_ratio', 'total_liabilities', 'shareholders_funds'),
      };
    })
    .filter((d) => d.ratio != null);

  // Rendered under a SectionDivider — a message keeps the section from dangling.
  if (chartData.length === 0) {
    return <FinancialNoData state="metric-missing" metricLabel="Debt / Equity" cardTitle="Debt/Equity Ratio" />;
  }

  const avg = chartData.reduce((s, d) => s + (d.ratio ?? 0), 0) / chartData.length;

  return (
    <ChartCard
      title="Debt/Equity Ratio"
      subtitle="Total liabilities ÷ shareholders funds per year, with average reference line"
    >
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={chartData} margin={{ top: 16, right: 16, left: 8, bottom: 0 }}>
          <CartesianGrid {...gridProps} />
          <XAxis dataKey="year" {...xAxisProps} />
          <YAxis {...yAxisProps} width={52} tickFormatter={(v) => v?.toFixed(2)} domain={['auto', 'auto']} />
          <ReferenceLine y={0} {...zeroLineProps} />
          <ReferenceLine y={avg} {...refLineProps}
            label={{ value: `Avg ${avg.toFixed(2)}`, position: 'right', fill: CHART_INK.muted, fontSize: CHART_TYPE.axisTick }}
          />
          <Tooltip content={<ChartTooltip format={(v) => v.toFixed(3)} />} />
          <Bar dataKey="ratio" name="Debt/Equity" radius={[4, 4, 0, 0]} maxBarSize={BAR_MAX_SIZE}>
            {chartData.map((entry, i) => (
              <Cell
                key={i}
                fill={PEER_COLORS[0]}
                fillOpacity={entry.estimated ? ESTIMATED_FILL_OPACITY : 1}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
